/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable max-lines */
/* eslint-disable max-len */
import { LitElement, html } from 'lit';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import { viewport } from '../../viewport';
import { timeout } from '../../utils/timeout';
import { wrap } from '../../utils/dom';
import { SplitText } from 'gsap/SplitText';
import { lazyload } from '../../inits/lazyload';

type ResponsiveImageResponse = {
    src: string;
    webp?: string;
    alt?: string;
    width?: number;
    height?: number;
};

type ResponsiveImage = {
    [mediaQuery: string]: ResponsiveImageResponse;
};

type Category = {
    tag: string;
    category: string;
    description: string;
    image: ResponsiveImage;
    item_image: ResponsiveImage;
    preview_image: ResponsiveImage;
    preview_title: string;
    price: string;
    id: string;
    category_url: string;
    item_endpoint: string;
    theme: 'light-pink' | 'light-blue' | 'aquamarine';
};

export interface CategoriesBlock {
    isMobile: boolean;
    data: Category[];
    id: string;
    sectionScrollHeightKoeff: number;
    sliderImages: HTMLElement[];
    sliderItemImages: HTMLElement[];
    sliderItemsText: HTMLElement[];
    sliderPreviews: HTMLElement[];
    sliderCursors: HTMLElement[];
    activeSlideIndex: number;
    prevActiveSlideIndex: number;
    sliderElement: HTMLElement | null;
    triggers: HTMLElement[];
    wrapper: HTMLElement | null;
    categoryLinkEl: HTMLElement | null;
    itemLinkEl: HTMLElement | null;
    cursors: any[];
    cursorElement: HTMLElement | null;
    cursorField: HTMLElement | null;
}

export class CategoriesBlock extends LitElement {
    constructor() {
        super();
        this._onResize = this._onResize.bind(this);
        this.data = this.getAttribute('category-data') ? JSON.parse(this.getAttribute('category-data')!) : null;
        this.sectionScrollHeightKoeff = viewport.width > 1920 ? 0.5 : 0.75;
        this.activeSlideIndex = 0;
        this.prevActiveSlideIndex = 0;

        this.cursors = [
            html`
                <svg width="225" height="225" viewBox="0 0 225 225" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M47.3468 205.59C67.2959 209.108 86.6518 201.26 98.9179 186.658C105.45 204.575 120.954 218.569 140.904 222.087C170.818 227.361 199.398 207.081 204.739 176.789C208.169 157.338 201.182 138.488 187.822 126.091C205.144 119.224 218.649 103.661 222.153 83.7887C227.494 53.4969 207.574 24.6646 177.66 19.3899C157.357 15.81 137.669 24.0017 125.443 39.1056C119.121 20.7312 103.421 6.29966 83.1183 2.71971C53.2041 -2.55497 24.6239 17.7254 19.2827 48.0172C15.8161 67.6773 22.9905 86.7227 36.6318 99.1118C19.5758 106.088 6.32018 121.531 2.85357 141.191C-2.48769 171.483 17.4326 200.315 47.3468 205.59Z"
                        fill="#FD5F42"
                    />
                    <path
                        d="M88.6923 105.625L90.9584 92.7734L80.9252 91.0043L78.6591 103.856L80.4672 104.175L82.4582 92.883L88.8752 94.0145L86.8842 105.306L88.6923 105.625ZM100.302 103.304C101.032 100.307 99.7406 97.5754 96.8157 97.0596C94.2099 96.6002 91.8515 98.1583 91.317 101.19C90.8201 104.008 92.105 106.465 95.1185 106.996C97.4407 107.405 99.2059 106.309 99.9189 104.753L98.2526 104.46C97.6792 105.327 96.7867 105.828 95.3686 105.578C93.9682 105.331 92.6542 104.076 92.9636 102.01L100.302 103.304ZM96.5719 98.4423C98.2914 98.7455 99.0288 100.265 98.8444 101.621L93.2251 100.63C93.7045 98.7409 95.1538 98.1923 96.5719 98.4423ZM102.691 98.2601L100.462 110.899L102.076 111.184L102.876 106.646C103.283 107.759 104.16 108.59 105.702 108.862C107.9 109.25 110.095 107.791 110.654 104.617C111.204 101.498 109.65 99.3227 107.452 98.9351C105.91 98.6632 104.802 99.1441 104.039 100.051L104.304 98.5446L102.691 98.2601ZM103.386 103.336C103.83 100.819 105.365 99.9927 106.801 100.246C108.449 100.537 109.302 102.021 108.899 104.308C108.496 106.595 107.186 107.698 105.538 107.407C104.102 107.154 102.942 105.853 103.386 103.336ZM120.497 106.865C121.226 103.868 119.935 101.136 117.01 100.62C114.404 100.161 112.046 101.719 111.511 104.75C111.014 107.569 112.299 110.025 115.313 110.557C117.635 110.966 119.4 109.87 120.113 108.314L118.447 108.02C117.874 108.888 116.981 109.389 115.563 109.139C114.163 108.892 112.849 107.636 113.158 105.571L120.497 106.865ZM116.766 102.003C118.486 102.306 119.223 103.825 119.039 105.182L113.42 104.191C113.899 102.302 115.348 101.753 116.766 102.003ZM129.178 99.5125C129.007 100.067 128.473 100.503 127.427 100.319C126.381 100.134 126.047 99.5453 126.076 98.9656L124.817 98.7436C124.718 99.8229 125.272 101.035 127.222 101.379C129.243 101.736 130.107 100.773 130.437 99.7345L129.178 99.5125ZM127.745 112.511L129.358 112.796L131.04 103.259L129.427 102.974L123.23 109.302L124.499 102.105L122.885 101.821L121.204 111.358L122.817 111.642L129.014 105.314L127.745 112.511ZM140.033 106.288L140.28 104.888L132.143 103.453L131.896 104.854L135.158 105.429L133.723 113.565L135.336 113.85L136.771 105.713L140.033 106.288ZM146.243 115.773L147.856 116.057L149.538 106.52L147.924 106.236L141.727 112.564L142.997 105.367L141.383 105.083L139.702 114.62L141.315 114.904L147.512 108.576L146.243 115.773Z"
                        fill="white"
                    />
                    <path
                        d="M91.5976 127.451L117.203 131.966M117.203 131.966L107.584 120.149M117.203 131.966L103.972 139.82"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            `,
            html`
                <svg width="237" height="216" viewBox="0 0 237 216" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M17.5778 39.2325C6.28905 41.8928 -0.705761 53.2007 1.95447 64.4894L13.059 111.612C15.4136 121.604 24.5428 128.231 34.4372 127.778C25.6765 131.926 20.6908 141.755 23.0037 151.57L34.1082 198.692C36.7685 209.981 48.0764 216.976 59.3652 214.316L219.466 176.587C230.755 173.927 237.749 162.619 235.089 151.331L223.985 104.208C221.63 94.2164 212.501 87.5886 202.606 88.0421C211.367 83.8944 216.353 74.065 214.04 64.25L202.935 17.1277C200.275 5.83892 188.967 -1.15589 177.678 1.50434L17.5778 39.2325Z"
                        fill="#814DBB"
                    />
                    <path
                        d="M92.5641 107.654L89.5708 94.9523L79.6544 97.2891L82.6477 109.991L84.4348 109.57L81.8048 98.4097L88.1471 96.9151L90.777 108.075L92.5641 107.654ZM102.314 100.937C101.801 97.8956 99.5355 95.8958 96.6447 96.577C94.0692 97.1839 92.5178 99.5468 93.2238 102.543C93.8803 105.328 96.0309 107.078 99.0093 106.376C101.304 105.835 102.493 104.131 102.534 102.42L100.887 102.808C100.703 103.832 100.081 104.644 98.679 104.974C97.2949 105.3 95.592 104.666 95.0606 102.646L102.314 100.937ZM96.9667 97.9436C98.6662 97.5431 99.9436 98.6476 100.31 99.9667L94.756 101.275C94.4503 99.3503 95.5651 98.2739 96.9667 97.9436ZM102.517 95.3597L105.46 107.852L107.055 107.476L105.998 102.991C106.811 103.853 107.945 104.27 109.469 103.911C111.642 103.399 113.082 101.192 112.343 98.0557C111.616 94.9722 109.33 93.5877 107.157 94.0997C105.633 94.4589 104.805 95.3383 104.462 96.4732L104.111 94.984L102.517 95.3597ZM105.16 99.7485C104.573 97.2606 105.657 95.8956 107.076 95.5612C108.706 95.1773 110.076 96.2044 110.608 98.4645C111.141 100.725 110.374 102.255 108.744 102.639C107.325 102.974 105.746 102.236 105.16 99.7485ZM122.273 96.2335C121.76 93.1921 119.495 91.1923 116.604 91.8736C114.028 92.4805 112.477 94.8433 113.183 97.8393C113.84 100.625 115.99 102.374 118.969 101.672C121.264 101.132 122.453 99.4275 122.493 97.7165L120.846 98.1046C120.662 99.1282 120.04 99.9405 118.638 100.271C117.254 100.597 115.551 99.9627 115.02 97.9427L122.273 96.2335ZM116.926 93.2401C118.625 92.8397 119.903 93.9441 120.269 95.2633L114.715 96.572C114.41 94.6468 115.524 93.5704 116.926 93.2401ZM127.345 86.0506C127.407 86.6277 127.089 87.239 126.055 87.4826C125.022 87.7262 124.481 87.3172 124.279 86.7731L123.035 87.0662C123.371 88.0968 124.358 88.9921 126.286 88.538C128.283 88.0673 128.697 86.8417 128.589 85.7575L127.345 86.0506ZM131.162 98.5586L132.756 98.1829L130.535 88.7571L128.941 89.1328L125.746 97.3937L124.07 90.2806L122.476 90.6563L124.697 100.082L126.291 99.7064L129.486 91.4455L131.162 98.5586ZM139.994 87.9891L139.668 86.605L131.626 88.5001L131.952 89.8841L135.176 89.1245L137.071 97.1662L138.665 96.7905L136.77 88.7488L139.994 87.9891ZM149.444 94.2503L151.039 93.8746L148.818 84.4488L147.223 84.8245L144.029 93.0854L142.353 85.9722L140.758 86.348L142.98 95.7738L144.574 95.3981L147.768 87.1371L149.444 94.2503Z"
                        fill="white"
                    />
                    <path
                        d="M108.55 125.942L133.857 119.978M133.857 119.978L120.353 112.92M133.857 119.978L124.803 132.419"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            `,
            html`
                <svg width="230" height="205" viewBox="0 0 230 205" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M2.56176 49.5976C0.925585 38.1156 8.9072 27.4813 20.3892 25.8451L183.739 2.56787C195.221 0.931697 205.855 8.91331 207.492 20.3953L226.678 155.035C228.314 166.517 220.332 177.152 208.85 178.788L45.5004 202.065C34.0184 203.701 23.384 195.719 21.7478 184.237L2.56176 49.5976Z"
                        fill="#EC87FF"
                    />
                    <path
                        d="M87.7688 102.101L85.9278 89.1817L75.8417 90.619L77.6827 103.538L79.5003 103.279L77.8828 91.9282L84.3336 91.0089L85.9512 102.36L87.7688 102.101ZM98.0822 96.2861C97.844 93.211 95.7676 91.016 92.8273 91.4349C90.2077 91.8082 88.4505 94.0223 88.8847 97.0695C89.2885 99.9029 91.2734 101.838 94.3028 101.407C96.6372 101.074 97.9742 99.4833 98.1682 97.783L96.4931 98.0217C96.2179 99.0245 95.5252 99.7778 94.0996 99.9809C92.6918 100.182 91.0527 99.3969 90.7047 97.3374L98.0822 96.2861ZM93.0253 92.8249C94.7539 92.5786 95.927 93.7932 96.1734 95.1399L90.5245 95.9449C90.3928 94 91.5997 93.028 93.0253 92.8249ZM98.7847 90.7496L100.595 103.455L102.217 103.224L101.567 98.6623C102.3 99.5942 103.391 100.111 104.942 99.8905C107.152 99.5756 108.784 97.5066 108.329 94.3168C107.882 91.1805 105.729 89.5964 103.52 89.9113C101.969 90.1322 101.066 90.9337 100.622 92.0333L100.406 90.5186L98.7847 90.7496ZM101.023 95.3579C100.663 92.8275 101.865 91.5653 103.308 91.3596C104.965 91.1235 106.238 92.2694 106.565 94.5682C106.893 96.867 105.991 98.3227 104.334 98.5589C102.89 98.7646 101.384 97.8884 101.023 95.3579ZM118.383 93.3933C118.145 90.3181 116.068 88.1231 113.128 88.5421C110.509 88.9154 108.751 91.1294 109.186 94.1766C109.589 97.01 111.574 98.9453 114.604 98.5137C116.938 98.181 118.275 96.5905 118.469 94.8901L116.794 95.1288C116.519 96.1317 115.826 96.8849 114.4 97.0881C112.993 97.2887 111.354 96.5041 111.006 94.4446L118.383 93.3933ZM113.326 89.932C115.055 89.6857 116.228 90.9004 116.474 92.2471L110.825 93.0521C110.694 91.1072 111.901 90.1352 113.326 89.932ZM124.349 83.7068C124.359 84.2872 123.987 84.8675 122.936 85.0173C121.884 85.1671 121.383 84.7113 121.23 84.1512L119.965 84.3315C120.207 85.388 121.11 86.3683 123.07 86.089C125.102 85.7995 125.624 84.616 125.614 83.5265L124.349 83.7068ZM127.027 96.5069L128.649 96.2758L127.283 86.6887L125.661 86.9198L121.738 94.8606L120.707 87.6257L119.086 87.8568L120.452 97.4439L122.073 97.2129L125.996 89.272L127.027 96.5069ZM136.772 86.7728L136.572 85.3651L128.392 86.5306L128.593 87.9384L131.872 87.4711L133.037 95.6505L134.659 95.4194L133.493 87.2401L136.772 86.7728ZM145.623 93.8571L147.244 93.626L145.878 84.0389L144.257 84.2699L140.334 92.2108L139.303 84.9759L137.681 85.207L139.047 94.7941L140.669 94.563L144.592 86.6222L145.623 93.8571Z"
                        fill="white"
                    />
                    <path
                        d="M102.049 121.749L127.789 118.081M127.789 118.081L114.973 109.84M127.789 118.081L117.655 129.66"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            `,
        ];
    }

    protected _splitCharsAndLines() {
        setTimeout(() => {
            const elements = this.querySelectorAll<HTMLElement>('[data-split-lines]');
            elements.forEach((element) => {
                const splitted = new SplitText(element, {
                    type: typeof element.dataset.splitChars !== 'undefined' ? 'chars,words,lines' : 'words,lines',
                    reduceWhiteSpace: true,
                });
                const { lines } = splitted;

                lines.forEach((lines) => {
                    const wrapper = wrap(lines);
                    wrapper.classList.add(
                        typeof element.dataset.splitChars !== 'undefined'
                            ? 'splitted-wrapper-chars'
                            : 'splitted-wrapper-lines',
                    );
                });
            });
        }, 1);
    }

    protected async _setActiveSlideText() {
        const newText = this.sliderItemsText[this.activeSlideIndex];

        const newChars = newText.querySelectorAll('.splitted-wrapper-chars > div > div > div');
        const newLines = newText.querySelectorAll('.splitted-wrapper-lines > div > div ');
        const newTag = newText.querySelector('.tag');

        if (this.prevActiveSlideIndex !== this.activeSlideIndex) {
            const prevText = this.sliderItemsText[this.prevActiveSlideIndex];

            const prevChars = prevText.querySelectorAll('.splitted-wrapper-chars > div > div > div');
            const prevLines = prevText.querySelectorAll('.splitted-wrapper-lines > div > div ');
            const prevTag = prevText.querySelector('.tag');

            const tl = gsap.timeline({ defaults: { duration: 0.25, stagger: 0.02 } });
            tl.to(prevChars, { y: '-150%', overwrite: true }, 0)
                .to(
                    prevLines,
                    {
                        y: '-150%',
                        overwrite: true,
                    },
                    0.2,
                )
                .to(prevTag, { y: '-110%' }, 0)
                .fromTo(newChars, { y: '150%' }, { y: '0%' }, 0.5)
                .fromTo(
                    newLines,
                    {
                        y: '150%',
                    },
                    { y: '0%' },
                    0.7,
                )
                .fromTo(newTag, { y: '-110%' }, { y: '0%' }, 0.7);
        } else {
            gsap.set(newChars, { y: '0%' });
            gsap.set(newLines, { y: '0%' });
            gsap.set(newTag, { y: '0%' });
        }
    }

    protected async _setActiveSlide(direction: number) {
        const newSlide = this.sliderImages[this.activeSlideIndex];
        const newSlideImageWrapper = newSlide.querySelector('.catalog-section-image-wrapper');
        const newSlideImage = newSlide.querySelector('.catalog-section-image');
        const newSlideCurtain = newSlide.querySelector('.slide-curtain');
        const newSlideCurtainInner = newSlide.querySelector('.slide-curtain__inner');

        if (this.prevActiveSlideIndex !== this.activeSlideIndex) {
            const prevSlide = this.sliderImages[this.prevActiveSlideIndex];
            const prevSlideImageWrapper = prevSlide.querySelector('.catalog-section-image-wrapper');
            const prevSlideImage = prevSlide.querySelector('.catalog-section-image');

            const tl = gsap.timeline({ defaults: { duration: 1 } });

            tl.set(prevSlide, {
                pointerEvents: 'none',
            })
                .set(newSlide, {
                    pointerEvents: 'auto',
                })
                .fromTo(
                    newSlideImageWrapper,
                    {
                        x: `${100 * direction * -1}%`,
                    },
                    { x: '0%' },
                    0,
                )
                .fromTo(
                    newSlideImage,
                    {
                        x: `${100 * direction}%`,
                    },
                    {
                        x: '0%',
                    },
                    0,
                )
                .fromTo(
                    prevSlideImageWrapper,
                    {
                        x: '0%',
                    },
                    {
                        x: `${100 * direction}%`,
                    },
                    0,
                )
                .fromTo(
                    prevSlideImage,
                    {
                        x: '0%',
                    },
                    {
                        x: `${100 * direction * -1}%`,
                    },
                    0,
                )
                .fromTo(newSlideCurtain, { x: `${100 * direction * -1}%` }, { x: '0%' }, 0)
                .set(
                    newSlideCurtainInner,
                    {
                        transformOrigin: direction === 1 ? 'right' : 'left',
                        left: direction === 1 ? 'unset' : '0',
                        right: direction === 1 ? '0' : 'unset',
                    },
                    0,
                )
                .to(newSlideCurtainInner, { scaleX: 0.15, duration: 0.4 }, 0)
                .to(newSlideCurtainInner, { scaleX: 0.0, duration: 0.4 }, 0.6);
        } else {
            gsap.set(newSlideImageWrapper, { x: '0%' });
            gsap.set(newSlideImage, { x: '0%' });
            gsap.set(newSlide, {
                pointerEvents: 'auto',
            });
        }
    }

    protected async _setActiveSlideImage(direction = 1) {
        const currentSlideImage = this.sliderItemImages[this.activeSlideIndex];

        if (this.prevActiveSlideIndex !== this.activeSlideIndex) {
            const prevSlideImage = this.sliderItemImages[this.prevActiveSlideIndex];
            const tl = gsap.timeline();
            tl.to(prevSlideImage, { y: `${25 * direction * -1}%`, opacity: 0, duration: 1, overwrite: true }, 0)
                .fromTo(
                    currentSlideImage,
                    {
                        y: `${25 * direction}%`,
                        duration: 1,
                    },
                    {
                        y: 0,
                        duration: 1,
                    },
                    0.3,
                )
                .fromTo(
                    currentSlideImage,
                    {
                        opacity: 0,
                        duration: 0.5,
                    },
                    {
                        opacity: 1,
                    },
                    0.3,
                );
        } else {
            gsap.set(currentSlideImage, { y: 0, opacity: 1 });
        }
    }

    protected async _setActiveSlidePreview(direction = 1) {
        const currentSlidePreview = this.sliderPreviews[this.activeSlideIndex];

        if (this.prevActiveSlideIndex !== this.activeSlideIndex) {
            const prevSlidePreview = this.sliderPreviews[this.prevActiveSlideIndex];
            const tl = gsap.timeline();
            tl.to(prevSlidePreview, { y: `${25 * direction * -1}%`, opacity: 0, duration: 1, overwrite: true }, 0)
                .fromTo(
                    currentSlidePreview,
                    {
                        y: `${25 * direction}%`,
                        duration: 1,
                    },
                    {
                        y: 0,
                        duration: 1,
                    },
                    0.3,
                )
                .fromTo(
                    currentSlidePreview,
                    {
                        opacity: 0,
                        duration: 0.5,
                    },
                    {
                        opacity: 1,
                    },
                    0.3,
                );
        } else {
            gsap.set(currentSlidePreview, { y: 0, opacity: 1 });
        }
    }

    protected async _setActiveSlideCursor() {
        const currentCursor = this.sliderCursors[this.activeSlideIndex];

        if (this.prevActiveSlideIndex !== this.activeSlideIndex) {
            const prevCursor = this.sliderCursors[this.prevActiveSlideIndex];
            prevCursor.classList.remove('is-active');
            currentCursor.classList.add('is-active');
        } else {
            currentCursor.classList.add('is-active');
        }
    }

    protected _setActiveAndPrevIndexes(index: number, direction: number) {
        this.activeSlideIndex = index;
        const promise = new Promise((resolve) => {
            this._setActiveSlide(direction);
            this._setActiveSlideImage(direction);
            this._setActiveSlidePreview(direction);
            this._setActiveSlideCursor();
            this._setActiveSlideText();
            resolve(true);
        });

        promise.then(() => {
            this.prevActiveSlideIndex = this.activeSlideIndex;
        });
    }

    protected _onSlideChange(instance: globalThis.ScrollTrigger) {
        const indexOfActiveSlider = this.triggers.findIndex((trigger) => trigger === instance.trigger);
        this._setActiveAndPrevIndexes(indexOfActiveSlider, instance.direction);

        const trigger = instance.trigger as HTMLElement;

        const { theme, categoryUrl, itemEndpoint } = trigger.dataset;

        if (this.wrapper) {
            this.wrapper.dataset.theme = theme;
        }

        if (this.categoryLinkEl && categoryUrl) {
            this.categoryLinkEl.setAttribute('href', categoryUrl);
        }

        if (this.itemLinkEl && itemEndpoint) {
            this.itemLinkEl.dataset.endpoint = itemEndpoint;
        }
    }

    protected _onMouseEnter() {
        this.cursorElement = document.querySelector<HTMLElement>('.js-cursor');
        this.cursorField = document.querySelector<HTMLElement>('.js-cursor-field');
        this.cursorElement?.classList.add('is-visible');
        this.cursorField?.classList.add('is-cursor-visible');
    }

    protected _onMouseLeave() {
        this.cursorElement = document.querySelector<HTMLElement>('.js-cursor');
        this.cursorField = document.querySelector<HTMLElement>('.js-cursor-field');
        this.cursorElement?.classList.remove('is-visible');
        this.cursorField?.classList.remove('is-cursor-visible');
    }

    protected _onPointerMove(event: MouseEvent) {
        this.cursorElement = document.querySelector<HTMLElement>('.js-cursor');
        if (this.cursorElement) {
            this.cursorElement.style.setProperty('--cursor-x', `${event.clientX}px`);
            this.cursorElement.style.setProperty('--cursor-y', `${event.clientY}px`);
        }
    }

    protected _initHandleCursor() {
        if (this.cursorField && this.cursorElement) {
            this.cursorField.addEventListener('mouseenter', this._onMouseEnter);
            this.cursorField.addEventListener('mouseleave', this._onMouseLeave);
            this.cursorField.addEventListener('pointermove', this._onPointerMove);
        }
    }

    protected _destroyHandleCursor() {
        if (this.cursorField && this.cursorElement) {
            this.cursorField.removeEventListener('mouseenter', this._onMouseEnter);
            this.cursorField.removeEventListener('mouseleave', this._onMouseLeave);
            this.cursorField.removeEventListener('pointermove', this._onPointerMove);
        }
    }

    protected async _initScrollTrigger() {
        await timeout(500);
        this.triggers = Array.from(document.querySelectorAll<HTMLElement>('.js-trigger-section'));
        this.wrapper = document.querySelector<HTMLElement>('.js-catalog-section-wrapper');
        this.sliderElement = document.querySelector<HTMLElement>('.js-catalog-slider');
        this.sliderImages = Array.from(document.querySelectorAll<HTMLElement>('.js-catalog-slider-item'));
        this.sliderItemImages = Array.from(document.querySelectorAll<HTMLElement>('.js-catalog-slider-item-image'));
        this.sliderItemsText = Array.from(document.querySelectorAll<HTMLElement>('.js-catalog-text-slide'));
        this.sliderPreviews = Array.from(document.querySelectorAll<HTMLElement>('.js-catalog-item-preview'));
        this.sliderCursors = Array.from(document.querySelectorAll<HTMLElement>('.js-cursor-inner'));
        this.cursorElement = document.querySelector<HTMLElement>('.js-cursor');
        this.cursorField = document.querySelector<HTMLElement>('.js-cursor-field');
        this.categoryLinkEl = document.querySelector('.js-category-link');
        this.itemLinkEl = document.querySelector('.js-item-link');

        this._initHandleCursor();

        if (this.wrapper) {
            this.wrapper.style.height = `${100 * this.sectionScrollHeightKoeff * (this.data.length + 2)}vh`;
            ScrollTrigger.refresh();
        }

        this.triggers.forEach((trigger, i) => {
            trigger.style.top = `${i * 100 * this.sectionScrollHeightKoeff}vh`;
            ScrollTrigger.create({
                trigger,
                start: 'top top',
                end: () => `+=${viewport.height * this.sectionScrollHeightKoeff}`,
                onEnter: (instance) => {
                    this._onSlideChange(instance);
                    this.wrapper?.classList.remove('cursor-hidden');
                },
                onEnterBack: (instance) => {
                    this._onSlideChange(instance);
                    this.wrapper?.classList.remove('cursor-hidden');
                },
                onLeave: () => {
                    if (i === this.triggers.length - 1) {
                        this.prevActiveSlideIndex = i;
                        this.wrapper?.classList.add('cursor-hidden');
                    }
                },
                onLeaveBack: () => {
                    if (i === 0) {
                        this.prevActiveSlideIndex = i;
                        this.wrapper?.classList.add('cursor-hidden');
                    }
                },
            });
        });
        this._setActiveAndPrevIndexes(0, 1);
    }

    protected _destroyScrollTrigger() {
        this.sliderImages = [];
        this._destroyHandleCursor();
    }

    protected _handleResizeForScrollTrigger() {
        setTimeout(() => {
            if (this.isMobile) {
                this._destroyScrollTrigger();
            } else {
                this._initScrollTrigger();
            }
        }, 1);
    }

    protected _onResize() {
        this.isMobile = window.matchMedia('(max-width: 1023px)').matches;
        this._handleResizeForScrollTrigger();
        setTimeout(() => {
            lazyload.update();
        }, 1);
    }

    static get properties() {
        return {
            isMobile: {
                type: Boolean,
                reflect: true,
            },
            projecting: { type: Boolean, reflect: true },
        };
    }

    createRenderRoot() {
        /**
         * Render template without shadow DOM. Note that shadow DOM features like
         * encapsulated CSS and slots are unavailable.
         */
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this._onResize);
        this._onResize();
        this._splitCharsAndLines();
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
    }

    protected _render() {
        if (this.isMobile) {
            return html`
                ${this.data.map((item) => {
                    return html`
                        <div class="catalog-section-wrapper js-catalog-section-wrapper">
                            <section class="catalog-section js-catalog-section catalog-section--${item.theme}">
                                <a href="${item.category_url}" class="catalog-section__left catalog-section__col">
                                    <div class="catalog-section__left-inner">
                                        <div class="catalog-section__tag tag">${item.tag}</div>
                                        <div class="catalog-section__category h2">${item.category}</div>
                                        <div class="catalog-section__description text-xl">${item.description}</div>
                                    </div>
                                    <div class="catalog-section__image-wrapper a-catalog-section-image-wrapper">
                                        <div class="catalog-section__image a-catalog-section-image">
                                            <picture>
                                                ${Object.keys(item.image).map((media) => {
                                                    return html`
                                                        <source
                                                            media="${media !== 'original'
                                                                ? `(max-width: ${media})`
                                                                : `(min-width: 1920px)`}"
                                                            data-srcset="${item.image[media].src}"
                                                        />
                                                        ${item.image[media].webp
                                                            ? html`<source
                                                                  media="${media !== 'original'
                                                                      ? `(max-width: ${media})`
                                                                      : `(min-width: 1920px)`}"
                                                                  data-srcset="${item.image[media].webp}"
                                                                  type="image/webp"
                                                              />`
                                                            : null}
                                                    `;
                                                })}
                                                ${item.image.original.webp
                                                    ? html`
                                                          <source
                                                              data-srcset="${item.image.original.src}"
                                                              type="image/webp"
                                                          />
                                                      `
                                                    : null}
                                                <img
                                                    data-src="${item.image.original.src}"
                                                    alt="${item.image.original.alt}"
                                                    width="${item.image.original.width}"
                                                    height="${item.image.original.height}"
                                                    class="lazy"
                                                />
                                            </picture>
                                        </div>
                                    </div>
                                </a>
                                <button
                                    class="catalog-section__right catalog-section__col"
                                    data-lit-popup-open="detail-card"
                                    data-endpoint="${item.item_endpoint}"
                                >
                                    <div class="catalog-section__item-image a-catalog-item-image">
                                        <picture>
                                            ${Object.keys(item.item_image).map((media) => {
                                                return html`
                                                    <source
                                                        media="${media !== 'original'
                                                            ? `(max-width: ${media})`
                                                            : `(min-width: 1920px)`}"
                                                        data-srcset="${item.item_image[media].src}"
                                                    />
                                                    ${item.item_image[media].webp
                                                        ? html`<source
                                                              media="${media !== 'original'
                                                                  ? `(max-width: ${media})`
                                                                  : `(min-width: 1920px)`}"
                                                              data-srcset="${item.item_image[media].webp}"
                                                              type="image/webp"
                                                          />`
                                                        : null}
                                                `;
                                            })}
                                            ${item.item_image.original.webp
                                                ? html`
                                                      <source
                                                          data-srcset="${item.item_image.original.src}"
                                                          type="image/webp"
                                                      />
                                                  `
                                                : null}
                                            <img
                                                data-src="${item.item_image.original.src}"
                                                alt="${item.item_image.original.alt}"
                                                width="${item.item_image.original.width}"
                                                height="${item.item_image.original.height}"
                                                class="lazy"
                                            />
                                        </picture>
                                    </div>
                                    <div class="catalog-section-item__curtain a-catalog-curtain"></div>
                                    <div
                                        class="catalog-section__preview catalog-section-preview js-catalog-item-preview"
                                    >
                                        <div class="catalog-section-preview__image">
                                            <picture>
                                                ${Object.keys(item.preview_image).map((media) => {
                                                    return html`
                                                        <source
                                                            media="${media !== 'original'
                                                                ? `(max-width: ${media})`
                                                                : `(min-width: 1920px)`}"
                                                            data-srcset="${item.preview_image[media].src}"
                                                        />
                                                        ${item.preview_image[media].webp
                                                            ? html`<source
                                                                  media="${media !== 'original'
                                                                      ? `(max-width: ${media})`
                                                                      : `(min-width: 1920px)`}"
                                                                  data-srcset="${item.preview_image[media].webp}"
                                                                  type="image/webp"
                                                              />`
                                                            : null}
                                                    `;
                                                })}
                                                ${item.preview_image.webp
                                                    ? html`
                                                          <source
                                                              data-srcset="${item.preview_image.webp.src}"
                                                              type="image/webp"
                                                          />
                                                      `
                                                    : null}
                                                <img
                                                    data-src="${item.preview_image.src}"
                                                    width="75"
                                                    height="88"
                                                    class="lazy"
                                                    alt="${item.preview_title}"
                                                />
                                            </picture>
                                        </div>
                                        <div class="catalog-section-preview__info">
                                            <div class="catalog-section-preview__title h-text-md">
                                                ${item.preview_title}
                                            </div>
                                            <div class="catalog-section-preview__price text-xl">${item.price}</div>
                                        </div>
                                        <div class="catalog-section-preview__icon-wrapper">
                                            <div class="catalog-section-preview__icon">
                                                <svg
                                                    width="60"
                                                    height="60"
                                                    viewBox="0 0 60 60"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M20 30.2899H40M40 30.2899L31.1351 22.973M40 30.2899L31.1351 37.027"
                                                        stroke="white"
                                                        stroke-width="1.5"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            </section>
                        </div>
                    `;
                })}
            `;
        }
        return html`
            <div
                class="catalog-section-wrapper js-catalog-section-wrapper cursor-hidden"
                data-theme="${this.data[0].theme}"
            >
                <div class="catalog-section js-catalog-section-sticky-element">
                    <a
                        href="${this.data[0].category_url}"
                        class="catalog-section__left catalog-section__col js-category-link js-cursor-field"
                    >
                        <div class="catalog-section-cursor js-cursor">
                            ${this.data.map((item, i) => {
                                return html` <div class="catalog-section-cursor__inner js-cursor-inner">
                                    ${i > this.cursors.length - 1
                                        ? html`${this.cursors[i % this.cursors.length]}`
                                        : html`${this.cursors[i]}`}
                                </div>`;
                            })}
                        </div>
                        ${this.data.map((item) => {
                            return html`
                                <div class="catalog-section__left-inner js-catalog-text-slide">
                                    <div class="catalog-section__tag">
                                        <div class="tag">${item.tag}</div>
                                    </div>
                                    <div class="catalog-section__category h2" data-split-chars data-split-lines>
                                        ${item.category}
                                    </div>
                                    <div class="catalog-section__description text-xl" data-split-lines>
                                        ${item.description}
                                    </div>
                                </div>
                            `;
                        })}
                        <div class="catalog-section-slider">
                            <div class="catalog-section-slider__inner js-catalog-slider">
                                ${this.data.map((item, i) => {
                                    return html`
                                        <div
                                            class="catalog-section-slider__item js-catalog-slider-item"
                                            data-key="${i}"
                                        >
                                            <div class="catalog-section-image-wrapper">
                                                <div class="catalog-section-image">
                                                    <picture>
                                                        ${Object.keys(item.image).map((media) => {
                                                            return html`
                                                                <source
                                                                    media="${media !== 'original'
                                                                        ? `(max-width: ${media})`
                                                                        : `(min-width: 1920px)`}"
                                                                    data-srcset="${item.image[media].src}"
                                                                />
                                                                ${item.image[media].webp
                                                                    ? html`<source
                                                                          media="${media !== 'original'
                                                                              ? `(max-width: ${media})`
                                                                              : `(min-width: 1920px)`}"
                                                                          data-srcset="${item.image[media].webp}"
                                                                          type="image/webp"
                                                                      />`
                                                                    : null}
                                                            `;
                                                        })}
                                                        ${item.image.original.webp
                                                            ? html`
                                                                  <source
                                                                      data-srcset="${item.image.original.src}"
                                                                      type="image/webp"
                                                                  />
                                                              `
                                                            : null}
                                                        <img
                                                            data-src="${item.image.original.src}"
                                                            alt="${item.image.original.alt}"
                                                            width="${item.image.original.width}"
                                                            height="${item.image.original.height}"
                                                            class="lazy"
                                                        />
                                                    </picture>
                                                </div>
                                            </div>
                                            <div class="slide-curtain">
                                                <div class="slide-curtain__inner"></div>
                                            </div>
                                        </div>
                                    `;
                                })}
                            </div>
                        </div>
                    </a>
                    <button
                        data-lit-popup-open="detail-card"
                        data-endpoint="${this.data[0].item_endpoint}"
                        class="catalog-section__right catalog-section__col js-item-link"
                    >
                        <div class="catalog-section__item-image a-catalog-item-image">
                            ${this.data.map((item, i) => {
                                return html`
                                    <div
                                        class="catalog-slider-item-image-wrapper js-catalog-slider-item-image"
                                        data-i="${i}"
                                    >
                                        <div class="catalog-slider-item-image">
                                            <picture>
                                                ${Object.keys(item.item_image).map((media) => {
                                                    return html`
                                                        <source
                                                            media="${media !== 'original'
                                                                ? `(max-width: ${media})`
                                                                : `(min-width: 1920px)`}"
                                                            data-srcset="${item.item_image[media].src}"
                                                        />
                                                        ${item.item_image[media].webp
                                                            ? html`<source
                                                                  media="${media !== 'original'
                                                                      ? `(max-width: ${media})`
                                                                      : `(min-width: 1920px)`}"
                                                                  data-srcset="${item.item_image[media].webp}"
                                                                  type="image/webp"
                                                              />`
                                                            : null}
                                                    `;
                                                })}
                                                ${item.item_image.original.webp
                                                    ? html`
                                                          <source
                                                              data-srcset="${item.item_image.original.src}"
                                                              type="image/webp"
                                                          />
                                                      `
                                                    : null}
                                                <img
                                                    data-src="${item.item_image.original.src}"
                                                    alt="${item.item_image.original.alt}"
                                                    width="${item.item_image.original.width}"
                                                    height="${item.item_image.original.height}"
                                                    class="lazy"
                                                />
                                            </picture>
                                        </div>
                                    </div>
                                `;
                            })}
                        </div>
                        <div class="catalog-section-preview-wrapper">
                            ${this.data.map((item) => {
                                return html`
                                    <div
                                        class="catalog-section__preview catalog-section-preview js-catalog-item-preview"
                                    >
                                        <div class="catalog-section-preview-image-wrapper">
                                            <div class="catalog-section-preview__image">
                                                <picture>
                                                    <source
                                                        data-srcset="${item.preview_image.webp}"
                                                        type="image/webp"
                                                    />
                                                    <img
                                                        data-src="${item.preview_image.src}"
                                                        class="lazy"
                                                        alt="${item.preview_title}"
                                                    />
                                                </picture>
                                            </div>
                                        </div>
                                        <div class="catalog-section-preview__info">
                                            <div class="catalog-section-preview__title h-text-md" data-split-lines>
                                                ${item.preview_title}
                                            </div>
                                            <div class="catalog-section-preview__price text-xl" data-split-lines>
                                                ${item.price}
                                            </div>
                                        </div>
                                        <div class="catalog-section-preview__plus-icon">
                                            <svg
                                                width="21"
                                                height="20"
                                                viewBox="0 0 21 20"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <line
                                                    x1="0.752441"
                                                    y1="9.71326"
                                                    x2="19.2524"
                                                    y2="9.71326"
                                                    stroke="#A9AAC8"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                />
                                                <line
                                                    x1="10.4565"
                                                    y1="19.2501"
                                                    x2="10.4565"
                                                    y2="0.750122"
                                                    stroke="#A9AAC8"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                />
                                            </svg>
                                        </div>
                                        <div class="catalog-section-preview__icon-wrapper">
                                            <div class="catalog-section-preview__icon">
                                                <svg
                                                    width="60"
                                                    height="60"
                                                    viewBox="0 0 60 60"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M20 30.2899H40M40 30.2899L31.1351 22.973M40 30.2899L31.1351 37.027"
                                                        stroke="white"
                                                        stroke-width="1.5"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                `;
                            })}
                        </div>
                    </button>
                </div>
                ${this.data.map((item) => {
                    return html`
                        <span
                            class="sticky-section-trigger js-trigger-section"
                            data-preview-title="${item.preview_title}"
                            data-preview-price="${item.price}"
                            data-preview-image="${item.preview_image.src}"
                            data-preview-image-webp="${item.preview_image.webp}"
                            data-theme="${item.theme}"
                            data-category-url="${item.category_url}"
                            data-item-endpoint="${item.item_endpoint}"
                        ></span>
                    `;
                })}
            </div>
        `;
    }

    render() {
        return html`${this._render()}`;
    }
}
